import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { GitCompare, Cpu, Clock, DollarSign, FileDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";

const formatCost = (usd) => {
  const n = Number(usd) || 0;
  if (n === 0) return "$0.00";
  if (n < 0.01) return "<$0.01";
  return `$${n.toFixed(n < 1 ? 4 : 2)}`;
};

/**
 * Side-by-side model comparison. Sends one prompt to two models in parallel
 * via POST /api/ai/compare and renders each reply with its usage + cost.
 */
export default function AICompareDialog({
  open, onOpenChange, models = [], defaultLeft, defaultRight, initialPrompt = "",
}) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [leftId, setLeftId] = useState(defaultLeft);
  const [rightId, setRightId] = useState(defaultRight);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // { message, results:[], elapsed_ms }

  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt || "");
      setResult(null);
      if (defaultLeft) setLeftId(defaultLeft);
      if (defaultRight) setRightId(defaultRight);
    }
  }, [open, initialPrompt, defaultLeft, defaultRight]);

  const modelLabel = (id) => models.find((m) => m.id === id)?.label || id;

  const submit = async () => {
    const msg = prompt.trim();
    if (!msg) return;
    if (leftId === rightId) {
      toast.error("Elige dos modelos distintos");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/ai/compare", {
        message: msg,
        models: [leftId, rightId],
      });
      setResult(r.data);
    } catch (e) {
      logger.error("ai compare", e);
      toast.error(e?.response?.data?.detail || "No se pudo comparar");
    } finally {
      setRunning(false);
    }
  };

  const exportPdf = async () => {
    if (!result) return;
    try {
      const resp = await fetch(`${API}/ai/compare.pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: result.message,
          results: result.results,
          elapsed_ms: result.elapsed_ms,
        }),
      });
      if (!resp.ok) throw new Error(`pdf_http_${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `comparacion-ia-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      logger.error("compare pdf", e);
      toast.error("No se pudo exportar el PDF");
    }
  };

  const findResult = (id) => result?.results?.find((x) => x.model === id);

  const ModelColumn = ({ id, label, onChange, testid }) => {
    const res = findResult(id);
    return (
      <div className="flex flex-col rounded-xl border border-[#EBEBEA] bg-white overflow-hidden min-h-[280px]">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#EBEBEA] bg-[#FAFAF9]">
          <Cpu className="w-3.5 h-3.5 text-[#1C7C54]" />
          <select
            data-testid={testid}
            value={id}
            onChange={(e) => onChange(e.target.value)}
            disabled={running}
            className="flex-1 h-8 px-2 rounded-lg border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60 disabled:opacity-60 cursor-pointer"
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 p-3 overflow-y-auto text-sm text-[#0A110E]" data-testid={`compare-reply-${id}`}>
          {running && !result && (
            <div className="text-xs text-[#8A9992] flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992] animate-pulse" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992] animate-pulse [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#8A9992] animate-pulse [animation-delay:300ms]" />
              <span className="ml-2">Consultando {label}…</span>
            </div>
          )}
          {res?.error && (
            <div className="text-xs text-[#D95D39]">Error: {res.error}</div>
          )}
          {res?.reply && (
            <div className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ul:my-1">
              <ReactMarkdown>{res.reply}</ReactMarkdown>
            </div>
          )}
          {!running && !res && (
            <div className="text-xs text-[#8A9992]">La respuesta aparecerá aquí.</div>
          )}
        </div>
        {res?.usage && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[#EBEBEA] text-[11px] text-[#5F7368] bg-[#FAFAF9]">
            <span>
              {res.usage.tokens_in.toLocaleString()} in · {res.usage.tokens_out.toLocaleString()} out
            </span>
            <span className="font-semibold text-[#0A110E]">
              {formatCost(res.usage.cost_usd)}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl bg-white" data-testid="ai-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-[#1C7C54]" />
            Comparar modelos
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              Pregunta
            </label>
            <Textarea
              data-testid="compare-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="¿En qué gasté más este mes? ¿Debería amortizar la hipoteca?"
              rows={2}
              className="mt-1"
            />
            <p className="text-[11px] text-[#8A9992] mt-1">
              La misma pregunta se enviará a los dos modelos con tus datos financieros como contexto.
              Esta comparación no se guarda en el historial.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ModelColumn
              id={leftId}
              label={modelLabel(leftId)}
              onChange={setLeftId}
              testid="compare-model-left"
            />
            <ModelColumn
              id={rightId}
              label={modelLabel(rightId)}
              onChange={setRightId}
              testid="compare-model-right"
            />
          </div>

          {result && (
            <div className="flex items-center justify-between text-[11px] text-[#8A9992]">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {result.elapsed_ms} ms en total (paralelo)
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Total combinado ·{" "}
                <span className="font-semibold text-[#0A110E] ml-1">
                  {formatCost(
                    (findResult(leftId)?.usage?.cost_usd || 0) +
                    (findResult(rightId)?.usage?.cost_usd || 0)
                  )}
                </span>
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          {result && (
            <Button
              variant="outline"
              onClick={exportPdf}
              data-testid="compare-export-pdf"
              className="border-[#1C7C54] text-[#1C7C54] hover:bg-[#1C7C54]/5"
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF
            </Button>
          )}
          <Button
            data-testid="compare-run-btn"
            onClick={submit}
            disabled={running || !prompt.trim() || leftId === rightId}
            className="bg-[#0F291E] hover:bg-[#1C7C54]"
          >
            {running ? "Comparando…" : "Comparar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
